/** OMNI WIKI: fuentes bibliográficas verificadas, sin duplicar ni alojar el PDF en la app. */
export interface OmniWikiHistoricalDictionary {
  readonly id: string;
  readonly title: string;
  readonly author: string;
  readonly edition: string;
  readonly year: number;
  readonly printPages: number;
  readonly uploadedScan: { readonly filename: string; readonly pages: number; readonly hosted: false };
  readonly publicFacsimile: { readonly pages: number; readonly url: string };
  readonly searchableEditionUrl: string;
  readonly note: string;
}

export const RAE_DICTIONARY_1884: OmniWikiHistoricalDictionary = Object.freeze({
  id: "rae-diccionario-lengua-castellana-1884",
  title: "Diccionario de la lengua castellana",
  author: "Real Academia Española",
  edition: "Duodécima edición",
  year: 1884,
  printPages: 1122,
  uploadedScan: Object.freeze({ filename: "Safari.pdf", pages: 1157, hosted: false as const }),
  publicFacsimile: Object.freeze({
    pages: 1156,
    url: "https://commons.wikimedia.org/wiki/File:Diccionario_de_la_lengua_castellana_(IA_diccionariodel00acaduoft).pdf"
  }),
  searchableEditionUrl: "https://edrae1884.uab.cat/edicion/",
  note: "El archivo aportado es una digitalización por imágenes sin capa de texto. La copia pública enlazada es otra reproducción de la misma edición, no el archivo adjunto."
});

export const WIKIPEDIA_ES_URL = "https://es.wikipedia.org/wiki/Wikipedia:Portada";


/** Rutas de búsqueda verificadas en la edición crítica digital e-DRAE 1884.
 * No existe una API pública de resultados confirmada: estos accesos NO aseguran
 * que se envíe la palabra al portal. El usuario la pega en el formulario fuente.
 */
export type HistoricalSearchMode = "lemas" | "etimologia" | "texto" | "comentarios";
export interface HistoricalSearchOption {
  readonly id: HistoricalSearchMode;
  readonly title: string;
  readonly description: string;
  readonly url: string;
}
export const HISTORICAL_SEARCH_OPTIONS: readonly HistoricalSearchOption[] = Object.freeze([
  { id: "lemas", title: "Lemas", description: "Buscar palabras y sus homógrafos",
    url: "https://edrae1884.uab.cat/edicion/" },
  { id: "etimologia", title: "Etimologías", description: "Origen histórico y lengua de procedencia",
    url: "https://edrae1884.uab.cat/edicion/etimologia" },
  { id: "texto", title: "Texto", description: "Buscar expresiones dentro del texto de la obra",
    url: "https://edrae1884.uab.cat/edicion/text" },
  { id: "comentarios", title: "Comentarios", description: "Consultar las anotaciones de la edición crítica",
    url: "https://edrae1884.uab.cat/edicion/comentaris" }
]);

export const HISTORICAL_FAVORITES_STORAGE_KEY = "omnisynia.omni-wiki.rae1884.bookmarks.v1";
export const RAE_1884_REFERENCE =
  "Real Academia Española (1884). Diccionario de la lengua castellana, duodécima edición. Madrid: Imprenta de D. Gregorio Hernando. Edición crítica digital: e-DRAE 1884 (UAB), https://edrae1884.uab.cat/edicion/";

/** Valida textos para marcadores y enlaces sin alterar letras acentuadas ni ñ. */
export function normalizeHistoricalTerm(input: string, maxLength = 100): string {
  const value = input.normalize("NFC").replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ").trim();
  return value.length <= maxLength ? value : "";
}

export function historicalSourceUrl(mode: HistoricalSearchMode): string {
  return HISTORICAL_SEARCH_OPTIONS.find(option => option.id === mode)?.url
    ?? RAE_DICTIONARY_1884.searchableEditionUrl;
}

/** Consulta al DLE actual; no la confundimos con una definición de 1884. */
export function modernDleUrl(term: string): string {
  const clean = normalizeHistoricalTerm(term);
  return clean ? "https://dle.rae.es/" + encodeURIComponent(clean) : "https://dle.rae.es/";
}

export function readHistoricalFavorites(value: string | null): string[] {
  if (!value || value.length > 10_000) return [];
  try {
    const items: unknown = JSON.parse(value);
    if (!Array.isArray(items)) return [];
    const favorites: string[] = [];
    for (const item of items.slice(0, 100)) {
      if (typeof item !== "string") continue;
      const term = normalizeHistoricalTerm(item);
      if (term && !favorites.some(previous => previous.toLocaleLowerCase("es") === term.toLocaleLowerCase("es"))) {
        favorites.push(term);
      }
      if (favorites.length === 20) break;
    }
    return favorites;
  } catch { return []; }
}

export function addHistoricalFavorite(current: readonly string[], input: string): string[] {
  const term = normalizeHistoricalTerm(input);
  if (!term) return [...current];
  return [term, ...current.filter(item => item.toLocaleLowerCase("es") !== term.toLocaleLowerCase("es"))].slice(0, 20);
}
